import { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TrustBadge } from "@/components/shared";
import { useMotionPresets, DUR, EASE } from "@/lib/motion";
import { useSignedFiles } from "@/hooks/useSignedFiles";
import { Sparkles, ChevronLeft, ChevronRight, Volume2 } from "lucide-react";

const SEV_BADGE = { high: "flag", medium: "medium", low: "high" };
const AUDIO_TONE = {
  suspicious: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  unclear: "text-muted-foreground bg-muted/40 border-border",
  benign: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
};

// Full-size, swipeable evidence viewer with the AI reason alongside each snapshot.
export function EvidenceLightbox({ items = [], index, onIndexChange, onClose, apiBase, token }) {
  const open = index !== null && index >= 0 && index < items.length;
  const M = useMotionPresets();
  const signUrl = useSignedFiles(items.map((i) => i.snapshot_path));
  const go = useCallback((d) => { if (items.length) onIndexChange((index + d + items.length) % items.length); }, [index, items.length, onIndexChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go]);

  if (!open) return null;
  const v = items[index];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-card border-border gap-0" data-testid="evidence-lightbox">
        <DialogTitle className="sr-only">Proctoring evidence viewer</DialogTitle>
        <DialogDescription className="sr-only">Full-size proctoring evidence with the AI reason and any audio assessment for this flag.</DialogDescription>
        <div className="grid md:grid-cols-[1.4fr_1fr]">
          <div className="relative bg-black min-h-[280px] flex items-center justify-center overflow-hidden">
            <AnimatePresence initial={false} mode="popLayout">
              <motion.img
                key={v.id || index}
                src={signUrl(v.snapshot_path)}
                alt={`Evidence: ${v.label || v.type}`}
                className="max-h-[70vh] w-full object-contain select-none"
                draggable={false}
                drag={items.length > 1 && !M.reduce ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.18}
                onDragEnd={(e, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); }}
                initial={M.reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1, transition: { duration: DUR.base, ease: EASE.out } }}
                exit={M.reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98, transition: { duration: DUR.fast } }}
                data-testid="lightbox-image"
              />
            </AnimatePresence>
            {items.length > 1 && (
              <>
                <button onClick={() => go(-1)} aria-label="Previous evidence" data-testid="lightbox-prev" className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/55 border border-white/15 text-white flex items-center justify-center hover:bg-black/75 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={() => go(1)} aria-label="Next evidence" data-testid="lightbox-next" className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/55 border border-white/15 text-white flex items-center justify-center hover:bg-black/75 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"><ChevronRight className="h-5 w-5" /></button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">{items.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === index ? "w-4 bg-white" : "w-1.5 bg-white/40"}`} />)}</div>
              </>
            )}
          </div>
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">{v.label || (v.type || "").replace(/_/g, " ")}</span>
              <TrustBadge type={SEV_BADGE[v.severity] || "medium"}>{v.severity}</TrustBadge>
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">{v.at ? new Date(v.at).toLocaleString() : ""} · {index + 1}/{items.length}</div>
            {v.ai_reason && <p className="text-xs text-primary/90 flex items-start gap-1.5" data-testid="lightbox-ai-reason"><Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {v.ai_reason}</p>}
            {v.detail && <p className="text-xs text-muted-foreground">{v.detail}</p>}
            {v.audio_assessment && (
              <div className="rounded-lg border border-border bg-muted/30 p-2.5 space-y-1.5" data-testid="lightbox-audio">
                <div className="flex items-center gap-1.5 flex-wrap"><Volume2 className="h-3.5 w-3.5 text-muted-foreground" /><span className={`text-[10px] px-1.5 py-0.5 rounded border ${AUDIO_TONE[v.audio_assessment.verdict] || AUDIO_TONE.unclear}`}>Audio: {v.audio_assessment.verdict}</span>{v.audio_assessment.voice?.verdict === "other_voice" && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${AUDIO_TONE.suspicious}`} data-testid="lightbox-voice">Voice: possible other voice</span>}</div>
                <p className="text-xs text-muted-foreground">{v.audio_assessment.reason}</p>
                {v.audio_transcript ? <p className="text-xs italic text-muted-foreground">“{v.audio_transcript}”</p> : null}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
